/** KB admin self-serve API client (FEATURE_KB_ADMIN). All requests carry the
 *  admin Bearer token. See openspec/kb-admin-self-serve.md §6. */
import { authHeaders } from "./auth";
import { getJson } from "./http";

export type KbSourceType = "upload" | "feishu" | "code";
export type Audience = "public" | "internal";
export type SyncPhase =
  | "queued" | "reading" | "indexing" | "replacing" | "done" | "aborted" | "needs_confirm" | "error";

export interface KbSyncResult {
  before: number;
  after: number;
  docs: number;
  skippedEmpty?: boolean;
  shrinkProspective?: number;
  badUrls?: string[];
  note?: string;
  attachments?: { parsed: number; unsupported: number; failed: number };
}

export interface KbSyncStatus {
  running: boolean;
  phase: SyncPhase;
  jobId?: string;
  startedAt?: string;
  finishedAt?: string;
  result?: KbSyncResult;
  error?: string;
}

export interface KbSourceRow {
  source: string;
  type: KbSourceType;
  dir: string | null;
  tenantScope: string;
  audience: Audience;
  feishuUrls: string[];
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  chunks: number;
  status: KbSyncStatus;
}

async function mutate<T>(url: string, method: string, body?: unknown): Promise<T> {
  // Only set Content-Type when there IS a body — a bodyless POST (e.g. /ingest)
  // with a JSON content-type trips Fastify's FST_ERR_CTP_EMPTY_JSON_BODY (400).
  const res = await fetch(url, {
    method,
    headers: body !== undefined ? authHeaders({ "Content-Type": "application/json" }) : authHeaders(),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} ${text.slice(0, 200)}`);
  }
  return (await res.json()) as T;
}

export const kbAdminApi = {
  list: (): Promise<{ sources: KbSourceRow[] }> => getJson("/api/console/kb/sources"),

  create: (input: {
    source: string;
    type: KbSourceType;
    tenantScope: string;
    audience: Audience;
    feishuUrls?: string[];
    files?: { name: string; content: string }[];
  }): Promise<{ source: KbSourceRow }> => mutate("/api/console/kb/sources", "POST", input),

  ingest: (source: string, force = false): Promise<{ source: string; jobId: string }> =>
    mutate(`/api/console/kb/sources/${encodeURIComponent(source)}/ingest${force ? "?force=1" : ""}`, "POST"),

  status: (source: string): Promise<KbSyncStatus & { source: string; chunks: number }> =>
    getJson(`/api/console/kb/sources/${encodeURIComponent(source)}/status`),

  remove: (source: string): Promise<{ source: string; removedChunks: number }> =>
    mutate(`/api/console/kb/sources/${encodeURIComponent(source)}`, "DELETE"),

  setAccess: (source: string, tenantScope: string, audience: Audience): Promise<unknown> =>
    mutate(`/api/console/kb/sources/${encodeURIComponent(source)}/access`, "PUT", { tenantScope, audience }),

  setUserClearance: (userId: string, clearance: Audience): Promise<unknown> =>
    mutate(`/api/console/kb/users/${encodeURIComponent(userId)}/clearance`, "PATCH", { clearance }),
};
